import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Download, Printer, MoreHorizontal } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PDoc, PTitle, PMeta, PKpis, PSection, PTable } from "@/components/print/PrintDoc";
import { PrintPageStyle } from "@/components/print/PrintFrontMatter";
import { daDate } from "@/lib/date";
import { fbT } from "@/components/quality/QrFilterBar";

/* Fælles server-filtersæt: URL-parametre → GET {path}?{params}. Liste, tal, PDF og Excel bruger samme svar. */
export function useServerView(path, { exportPath, defaults = {} } = {}) {
  const [rawParams, setParams] = useSearchParams();
  const params = new URLSearchParams(rawParams);
  Object.entries(defaults).forEach(([k, v]) => { if (!rawParams.has(k) && v) params.set(k, v); });
  const [data, setData] = useState(null);
  const req = useRef(0);
  const load = () => { const id = ++req.current; return api.get(`${path}${path.includes("?") ? "&" : "?"}${params.toString()}`).then((r) => { if (id === req.current) setData(r.data); }).catch((e) => { if (id === req.current) { setData({ rows: [], total: 0, options: {}, counts: {} }); toast.error(e.response?.data?.detail || "Kunne ikke hente"); } }); };
  useEffect(() => { load(); }, [params.toString(), path]); // eslint-disable-line
  const setParam = (patch) => setParams((prev) => { const n = new URLSearchParams(prev); Object.entries(defaults).forEach(([k, v]) => { if (!n.has(k) && v) n.set(k, v); }); Object.entries(patch).forEach(([k, v]) => { const val = Array.isArray(v) ? v.join(",") : v; if (val == null || val === "" || val === "og") { defaults[k] ? n.set(k, "alle") : n.delete(k); } else n.set(k, val); }); return n; });
  const getArr = (k) => { const v = params.get(k); return v && v !== "alle" ? v.split(",").filter(Boolean) : []; };
  const [search, setSearch] = useState(() => params.get("q") || "");
  const sT = useRef(null);
  useEffect(() => { const q = params.get("q") || ""; if (!sT.current && q !== search) setSearch(q); }, [params.toString()]); // eslint-disable-line
  const onSearch = (v) => { setSearch(v); clearTimeout(sT.current); sT.current = setTimeout(() => { sT.current = null; setParam({ q: v }); }, 250); };
  const exportXlsx = () => api.get(`${exportPath}${exportPath.includes("?") ? "&" : "?"}${params.toString()}`, { responseType: "blob" }).then((res) => {
    const m = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || ""); const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "eksport.xlsx"; a.click(); URL.revokeObjectURL(url);
  }).catch(() => toast.error("Kunne ikke eksportere"));
  const opt = (k) => (data?.options?.[k] || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const mkFilter = (key, label, extra = {}) => ({ key, label, options: opt(key), selected: getArr(key), onChange: (v) => setParam({ [key]: v }), ...extra });
  const period = { from: params.get("from") || "", to: params.get("to") || "", onChange: (f, t) => setParam({ from: f, to: t }) };
  const match = params.get("match") || "og";
  const reset = () => setParams(new URLSearchParams(Object.entries(defaults).filter(([, v]) => v)));
  return { data, rows: data?.rows || [], params, setParam, getArr, search, onSearch, exportXlsx, mkFilter, period, match, setMatch: (v) => setParam({ match: v }), reset, reload: load, opt };
}

export function activeFilters(view, labels) {
  const out = [];
  if (view.params.get("q")) out.push(["Søgning", view.params.get("q")]);
  Object.entries(labels).forEach(([k, lab]) => { const sel = view.getArr(k); if (sel.length) out.push([lab, sel.map((v) => (view.opt(k).find((o) => o.value === v) || { label: v }).label).join(", ")]); });
  if (view.period.from || view.period.to) out.push(["Periode", `${view.period.from ? daDate(view.period.from) : "…"} – ${view.period.to ? daDate(view.period.to) : "…"}`]);
  if (view.match === "eller") out.push(["Kombinér", "Eller"]);
  return out;
}

/* Sidehoved: desktop-knapper + mobil ⋯-menu. children = eksisterende knapper (Arkiverede, gamle eksporter) */
export function ListActions({ onExport, more = [], children, primary, t = null }) {
  const T = fbT(t);
  const print = () => window.print();
  return (
    <>
      <div className="hidden md:flex items-center gap-2">
        {children}
        <Button variant="outline" size="sm" className="rounded-xl border-[#EBE8E3] bg-white h-9" onClick={print} data-testid="list-print"><Printer className="h-4 w-4 mr-1.5" />{T("fb_pdf")}</Button>
        {onExport && <Button variant="outline" size="sm" className="rounded-xl border-[#EBE8E3] bg-white h-9" onClick={onExport} data-testid="list-export-view"><Download className="h-4 w-4 mr-1.5" />{T("fb_export_view")}</Button>}
        {more.filter((m) => !m.mobileOnly).length > 0 && <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-xl border-[#EBE8E3] bg-white h-9 px-2.5" data-testid="list-more" title={T("fb_more")}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl">{more.filter((m) => !m.mobileOnly).map((m) => <DropdownMenuItem key={m.label} onSelect={m.onSelect} data-testid={m.testid}><Download className="h-4 w-4 mr-1.5" />{m.label}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>}
      </div>
      <div className="md:hidden flex items-center gap-2">
        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-xl border-[#EBE8E3] bg-white h-9 px-2.5" data-testid="list-mobile-menu"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl min-w-[220px]">
            <DropdownMenuItem onSelect={print}><Printer className="h-4 w-4 mr-1.5" />{T("fb_pdf")}</DropdownMenuItem>
            {onExport && <DropdownMenuItem onSelect={onExport}><Download className="h-4 w-4 mr-1.5" />{T("fb_export_view")}</DropdownMenuItem>}
            {more.map((m) => <DropdownMenuItem key={m.label} onSelect={m.onSelect}><Download className="h-4 w-4 mr-1.5" />{m.label}</DropdownMenuItem>)}
            {children && <div className="px-2 py-1.5 border-t border-[#F1EEE9] mt-1 flex flex-wrap gap-2">{children}</div>}
          </DropdownMenuContent></DropdownMenu>
      </div>
      {primary}
    </>
  );
}

/* Fortællende PDF i DEL 1-stil: sidehoved/-fod m. logo, aktive filtre, nøgletal (beige + mørkt kritisk kort), tabel. */
export function PrintPortal({ children }) {
  useEffect(() => { document.body.classList.add("qr-print-page"); return () => document.body.classList.remove("qr-print-page"); }, []);
  return createPortal(<div className="print-root pub-print-doc">{children}</div>, document.body);
}

export const clamp2 = (v) => <span className="clamp2">{v}</span>;

export function ListPrint({ kicker, title, filters, kpis, cols, rows, summary, noun = "poster", extra = null }) {
  return (
    <PrintPortal>
      <PrintPageStyle title={title} date={daDate(new Date().toISOString())} />
      <PDoc className="qr-pdf">
        <PTitle kicker={kicker} title={title} sub={`Udskrevet ${daDate(new Date().toISOString())}`} />
        <PMeta title="Aktive filtre" items={filters.length ? filters.map(([k, v]) => ({ label: k, value: v })) : [{ label: "Filter", value: `Ingen – alle ${noun}` }]} />
        {kpis && <PKpis items={kpis} />}
        <PSection num="01" title="Sammenfatning">{summary}</PSection>
        <PSection num="02" title={title} empty={rows.length === 0 ? `Ingen ${noun} i udvalget.` : null}>
          <PTable cols={cols} rows={rows} split />
        </PSection>
        {extra}
      </PDoc>
    </PrintPortal>
  );
}
